import { Eye, EyeOff, RotateCcw } from "lucide-react";
import { IconButton } from "../../components/primitives";
import { SlackIcon } from "../../components/slack-icons";
import type { FormatAction } from "./editor-types";

export type { FormatAction };

export type FormatMode = "rich" | "inline-rich" | "mrkdwn" | "inline-mrkdwn" | "emoji" | "markdown" | "none";

const BLOCK_FORMATS = new Set<FormatAction>(["codeBlock", "bullet", "number", "quote"]);

export function supportsFormat(mode: FormatMode, action: FormatAction | "link") {
  if (mode === "rich") return true;
  if (mode === "inline-rich") return action === "link" || !BLOCK_FORMATS.has(action);
  if (mode === "mrkdwn")
    return action !== "underline" && action !== "bullet" && action !== "number" && action !== "clear";
  if (mode === "inline-mrkdwn")
    return action === "link" || (!BLOCK_FORMATS.has(action) && action !== "underline" && action !== "clear");
  if (mode === "markdown") return action !== "underline" && action !== "clear";
  return false;
}

export function supportsMentions(mode: FormatMode) {
  return mode === "rich" || mode === "inline-rich" || mode === "mrkdwn" || mode === "inline-mrkdwn";
}

export function supportsEmoji(mode: FormatMode) {
  return (
    mode === "rich" ||
    mode === "inline-rich" ||
    mode === "mrkdwn" ||
    mode === "inline-mrkdwn" ||
    mode === "emoji" ||
    mode === "markdown"
  );
}

const SHORTCUTS: Partial<Record<FormatAction | "link", string>> = {
  bold: "⌘B",
  italic: "⌘I",
  underline: "⌘U",
  strike: "⌘⇧X",
  link: "⌘⇧U",
  code: "⌘⇧C",
  codeBlock: "⌘⌥⇧C",
  bullet: "⌘⇧8",
  number: "⌘⇧7",
  quote: "⌘⇧9",
};

function formatLabel(label: string, action: FormatAction | "link", enabled: boolean, mode: FormatMode) {
  const suffix = enabled && SHORTCUTS[action] ? ` (${SHORTCUTS[action]})` : "";
  if (enabled) return `${label}${suffix}`;
  if (mode === "emoji") return `${label} isn’t supported in this Slack plain-text field`;
  return `${label} isn’t available for this field`;
}

export type FormatState = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  code: boolean;
  codeBlock: boolean;
  quote: boolean;
  bullet: boolean;
  ordered: boolean;
  link: boolean;
  partial: Partial<Record<"bold" | "italic" | "underline" | "strike", boolean>>;
};

export const EMPTY_FORMATS: FormatState = {
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  code: false,
  codeBlock: false,
  quote: false,
  bullet: false,
  ordered: false,
  link: false,
  partial: {},
};

function modeLabel(mode: FormatMode) {
  if (mode === "rich") return "Rich text";
  if (mode === "inline-rich") return "Rich text · one line";
  if (mode === "mrkdwn") return "Slack formatting";
  if (mode === "inline-mrkdwn") return "Slack formatting · one line";
  if (mode === "emoji") return "Plain text · emoji";
  if (mode === "markdown") return "Markdown source";
  return "Select a text field";
}

function ToolbarButton({
  action,
  label,
  mode,
  active,
  partial,
  onFormat,
  children,
}: {
  action: FormatAction;
  label: string;
  mode: FormatMode;
  active?: boolean;
  partial?: boolean;
  onFormat: (action: FormatAction) => void;
  children: React.ReactNode;
}) {
  if (!supportsFormat(mode, action)) return null;
  return (
    <IconButton
      label={formatLabel(label, action, true, mode)}
      active={active}
      partial={partial}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onFormat(action)}
    >
      {children}
    </IconButton>
  );
}

export function FormatToolbar({
  onLink,
  onFormat,
  onToggleUnfurls,
  unfurlLinks,
  mode,
  formats = EMPTY_FORMATS,
}: {
  onLink: () => void;
  onFormat: (action: FormatAction) => void;
  onToggleUnfurls: () => void;
  unfurlLinks: boolean;
  mode: FormatMode;
  formats?: FormatState;
}) {
  const keepFocus = (event: React.MouseEvent) => event.preventDefault();
  const hasInline = ["bold", "italic", "underline", "strike", "code"].some((action) =>
    supportsFormat(mode, action as FormatAction),
  );
  const hasBlocks = ["codeBlock", "bullet", "number", "quote"].some((action) =>
    supportsFormat(mode, action as FormatAction),
  );
  return (
    <div className="format-toolbar" role="toolbar" aria-label="Formatting">
      <span className="format-toolbar-mode">
        <SlackIcon name="formatting" size={14} />
        {modeLabel(mode)}
      </span>
      {hasInline && (
        <div className="format-toolbar-group">
          <ToolbarButton
            action="bold"
            label="Bold"
            mode={mode}
            active={formats.bold}
            partial={formats.partial.bold}
            onFormat={onFormat}
          >
            <SlackIcon name="bold" size={17} />
          </ToolbarButton>
          <ToolbarButton
            action="italic"
            label="Italic"
            mode={mode}
            active={formats.italic}
            partial={formats.partial.italic}
            onFormat={onFormat}
          >
            <SlackIcon name="italic" size={17} />
          </ToolbarButton>
          <ToolbarButton
            action="underline"
            label="Underline"
            mode={mode}
            active={formats.underline}
            partial={formats.partial.underline}
            onFormat={onFormat}
          >
            <SlackIcon name="underline" size={17} />
          </ToolbarButton>
          <ToolbarButton
            action="strike"
            label="Strikethrough"
            mode={mode}
            active={formats.strike}
            partial={formats.partial.strike}
            onFormat={onFormat}
          >
            <SlackIcon name="strikethrough" size={17} />
          </ToolbarButton>
          {supportsFormat(mode, "link") && (
            <IconButton
              label={formatLabel("Link", "link", true, mode)}
              active={formats.link}
              onMouseDown={keepFocus}
              onClick={onLink}
            >
              <SlackIcon name="link" size={17} />
            </IconButton>
          )}
          <ToolbarButton action="code" label="Inline code" mode={mode} active={formats.code} onFormat={onFormat}>
            <SlackIcon name="code" size={17} />
          </ToolbarButton>
        </div>
      )}
      {hasBlocks && (
        <div className="format-toolbar-group">
          <ToolbarButton action="bullet" label="Bulleted list" mode={mode} active={formats.bullet} onFormat={onFormat}>
            <SlackIcon name="bulletedList" size={17} />
          </ToolbarButton>
          <ToolbarButton action="number" label="Numbered list" mode={mode} active={formats.ordered} onFormat={onFormat}>
            <SlackIcon name="orderedList" size={17} />
          </ToolbarButton>
          <ToolbarButton action="quote" label="Quote" mode={mode} active={formats.quote} onFormat={onFormat}>
            <SlackIcon name="quote" size={17} />
          </ToolbarButton>
          <ToolbarButton
            action="codeBlock"
            label="Code block"
            mode={mode}
            active={formats.codeBlock}
            onFormat={onFormat}
          >
            <SlackIcon name="codeBlock" size={17} />
          </ToolbarButton>
        </div>
      )}
      <div className="format-toolbar-trailing">
        <IconButton
          label={unfurlLinks ? "Link previews shown" : "Link previews hidden"}
          active={!unfurlLinks}
          onMouseDown={keepFocus}
          onClick={onToggleUnfurls}
        >
          {unfurlLinks ? <Eye size={15} /> : <EyeOff size={15} />}
        </IconButton>
        {supportsFormat(mode, "clear") && (
          <IconButton label="Clear formatting" onMouseDown={keepFocus} onClick={() => onFormat("clear")}>
            <RotateCcw size={15} />
          </IconButton>
        )}
      </div>
    </div>
  );
}
